import { Component } from '@theme/component';
import { onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent, ThemeEvents } from '@theme/events';
import { positionHeaderMenuPanel } from './header-menu-position.js';

class HeaderImageDropdown extends Component {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {HTMLDetailsElement[]} */
  #dropdowns = [];
  /** @type {(() => void)[]} */
  #restore = [];
  /** @type {MutationObserver | undefined} */
  #observer;
  /** @type {Set<ReturnType<typeof setTimeout>>} */
  #timers = new Set();
  #reportedMissingParent = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Node)) return;
      for (const details of this.#dropdowns) {
        if (!details.contains(event.target)) details.open = false;
      }
    }, { signal: this.#controller.signal });
    window.addEventListener('resize', () => {
      for (const details of this.#dropdowns) {
        if (!details.open) continue;
        this.#positionPanel(details);
      }
    }, { signal: this.#controller.signal });
    onDocumentReady(() => {
      if (!this.isConnected) return;
      const header = this.closest('header-component');
      if (!header) return;
      header.addEventListener(ThemeEvents.megaMenuHover, (event) => {
        for (const details of this.#dropdowns) {
          if (event.target !== details) details.open = false;
        }
      }, { signal: this.#controller?.signal });
      this.#observer = new MutationObserver(() => this.#mount());
      this.#observer.observe(header, { childList: true, subtree: true });
      this.#mount();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    for (const timer of this.#timers) clearTimeout(timer);
    this.#timers.clear();
    for (const restore of this.#restore) restore();
    this.#restore = [];
    this.#dropdowns = [];
  }

  #mount() {
    if (!this.isConnected) return;

    const header = this.closest('header-component');
    const template = this.querySelector('template');
    if (!header || !(template instanceof HTMLTemplateElement)) return;

    const desktopLinks = Array.from(header.querySelectorAll('header-menu [ref="menuitem"]'));
    const mobileLinks = Array.from(header.querySelectorAll('.menu-drawer__menu-item--mainlist'));
    const handle = this.dataset.parentHandle;
    const position = Number(this.dataset.parentPosition) - 1;
    const desktop = handle
      ? desktopLinks.find((link) => link instanceof HTMLElement && link.dataset.menuHandle === handle)
      : desktopLinks.find((link) => link instanceof HTMLElement && Number(link.dataset.menuPosition) === position + 1);
    const mobile = handle
      ? mobileLinks.find((link) => link.id === `HeaderDrawer-${handle}`)
      : mobileLinks[position];

    if (!desktop && !mobile) {
      if (!this.#reportedMissingParent) {
        console.error('Image dropdown parent menu item not found', { handle, position: position + 1 });
        this.#reportedMissingParent = true;
      }
      return;
    }
    this.#reportedMissingParent = false;

    for (const link of [desktop, mobile]) {
      if (!(link instanceof HTMLElement)) continue;
      const item = link.closest('li');
      if (!item || item.querySelector('.header-image-dropdown')) continue;

      const fragment = template.content.cloneNode(true);
      if (!(fragment instanceof DocumentFragment)) continue;
      const details = fragment.querySelector('details');
      const summary = fragment.querySelector('summary');
      const panel = fragment.querySelector('.header-image-dropdown__panel');
      if (!details || !summary || !panel) continue;

      summary.className += ` ${link.className}`;
      if (link.id) summary.id = link.id;
      summary.textContent = link.textContent?.trim() || '';
      const indicator = document.createElement('span');
      indicator.className = 'header-image-dropdown__indicator';
      indicator.setAttribute('aria-hidden', 'true');
      indicator.textContent = '+';
      summary.append(indicator);
      if (link.getAttribute('ref') === 'menuitem') {
        summary.setAttribute('ref', 'menuitem');
        if (link.dataset.menuHandle) summary.dataset.menuHandle = link.dataset.menuHandle;
        if (link.dataset.menuPosition) summary.dataset.menuPosition = link.dataset.menuPosition;
      }
      const parentLink = link instanceof HTMLAnchorElement ? link : item.querySelector('a');
      if (parentLink instanceof HTMLAnchorElement) {
        const parent = parentLink.cloneNode(true);
        if (parent instanceof HTMLAnchorElement) {
          parent.removeAttribute('id');
          parent.removeAttribute('ref');
          parent.removeAttribute('aria-controls');
          parent.removeAttribute('aria-expanded');
          parent.removeAttribute('aria-haspopup');
          parent.className = 'header-image-dropdown__parent';
          const triggerLink = parent.cloneNode(true);
          if (triggerLink instanceof HTMLAnchorElement) {
            triggerLink.className = 'header-image-dropdown__trigger-link';
            summary.replaceChildren(triggerLink, indicator);
          }
          panel.prepend(parent);
        }
      }

      const originalNodes = Array.from(item.childNodes);
      const eventAttributes = ['on:focus', 'on:blur', 'on:pointerenter', 'on:pointerleave'];
      const attributes = eventAttributes.map((name) => ({ name, value: item.getAttribute(name) }));
      this.#restore.push(() => {
        if (!item.contains(details)) return;
        item.replaceChildren(...originalNodes);
        for (const { name, value } of attributes) {
          if (value !== null) item.setAttribute(name, value);
        }
      });
      for (const attribute of eventAttributes) {
        item.removeAttribute(attribute);
      }
      item.replaceChildren(fragment);
      this.#dropdowns.push(details);
      const signal = this.#controller?.signal;
      summary.setAttribute('aria-expanded', 'false');
      let openedByHover = false;
      /** @type {ReturnType<typeof setTimeout> | undefined} */
      let closeTimer;
      const cancelClose = () => {
        if (closeTimer === undefined) return;
        clearTimeout(closeTimer);
        this.#timers.delete(closeTimer);
        closeTimer = undefined;
      };
      if (link === desktop) {
        details.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          cancelClose();
          details.dispatchEvent(new MegaMenuHoverEvent());
          if (!details.open) {
            openedByHover = true;
            details.open = true;
            this.#positionPanel(details);
          }
        }, { signal });
        details.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          cancelClose();
          closeTimer = setTimeout(() => {
            cancelClose();
            if (!details.matches(':focus-within')) details.open = false;
          }, 250);
          this.#timers.add(closeTimer);
        }, { signal });
        summary.addEventListener('click', (event) => {
          cancelClose();
          if (event.target instanceof Element && event.target.closest('.header-image-dropdown__trigger-link')) return;
          if (openedByHover && event.detail > 0) {
            event.preventDefault();
            openedByHover = false;
          }
        }, { signal });
      }
      details.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        details.open = false;
        summary.focus();
      }, { signal });
      details.addEventListener('focusout', (event) => {
        if (event.relatedTarget instanceof Node && !details.contains(event.relatedTarget)) details.open = false;
      }, { signal });
      details.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', String(details.open));
        indicator.textContent = details.open ? '-' : '+';
        if (!details.open) openedByHover = false;
        if (!details.open) return;
        details.dispatchEvent(new MegaMenuHoverEvent());
        this.#positionPanel(details);
      }, { signal });
    }

  }

  /** @param {HTMLDetailsElement} details */
  #positionPanel(details) {
    const panel = details.querySelector('.header-image-dropdown__panel');
    const trigger = details.querySelector('summary');
    if (details.closest('.header-drawer')) return;
    if (panel instanceof HTMLElement && trigger instanceof HTMLElement) positionHeaderMenuPanel(panel, trigger);
  }
}

if (!customElements.get('header-image-dropdown')) {
  customElements.define('header-image-dropdown', HeaderImageDropdown);
}
